/**
 * The headshot frame — World's formula, mirrored (ADR-035 §3.1).
 *
 * World's 3D view opens on a broadcast-style head-and-shoulders shot, and the
 * hero card is that same frame. Both renderers must produce the SAME shot, so
 * the formula is data shared verbatim, not camera code invented twice. This
 * module is a MIRROR of World's `src/ui/avatar-preview/avatarPreviewPlan.ts`
 * (their PR #1640, published in the comms channel 2026-09-06 07:40 PT;
 * TIGHTENED 16:40 PT after Allen's first live look — "mostly head, face, a
 * little bit of shoulders, the crown very near the top" — their PR
 * `fix/1637-headshot-tighter`); every constant carries their exported name.
 * World said the formula will not move without a note in the comms first —
 * when it does, this file and its test are where the drift is reconciled.
 *
 * Two frames of reference, kept apart on purpose:
 *  - World computes in METRES after its scale rule: a package's body is
 *    scaled to `heightMeters`, feet at y = 0. `headshotFrame` takes metres.
 *  - Harry's subject stands in its own RIG UNITS, unscaled, wherever its
 *    file put it (a Tripo body is ~2 units tall with its feet near 0).
 *    `headshotLens` converts the metre frame into a lens in those units by
 *    the one ratio World applies — declared height over measured height — so
 *    a 2-unit body declared at 1.9 m is framed exactly as World frames it.
 *
 * Facing: World's stage faces the body toward −Z (Babylon) and its `alpha`
 * is −π/2. Harry's subjects keep the facing their rig DECLARES (a Tripo body
 * +Z, a VRM 0.x −Z by spec, a goblin promoted to +X by measurement — the
 * RigDescriptor's `facing`), so the camera sits on THAT axis: `alphaForFacing`
 * maps the facing to the ArcRotate azimuth, camera = target +
 * radius·(sinβ·cosα, cosβ, sinβ·sinα). A fixed +Z shot the back of a VRM
 * (Codex P1, #162).
 *
 * Depends on: nothing (pure). Depended on by: main (the 📷 headshot take, the
 * batch), tests.
 */

/** Below-head span, as a fraction of body height: "a little bit of shoulders" (was 0.25 — half a body — until 16:40 PT). */
export const HEADSHOT_BELOW_HEAD = 0.09;
/** Headroom above the crown, as a fraction of body height: the crown very near the top; a hat or horns still fit (was 0.06). */
export const HEADSHOT_HEADROOM = 0.02;
/** The portrait lens: vertical FOV in radians (~85 mm equivalent). */
export const HEADSHOT_FOV = 0.42;
/** Breathing room around the span (was 1.08). */
export const HEADSHOT_MARGIN = 1.04;
/** Never closer than this, in metres — a tiny body is not shot from inside its own nose. */
export const HEADSHOT_MIN_RADIUS_M = 0.6;
/** The frame never collapses below this span, in metres. */
export const HEADSHOT_MIN_SPAN_M = 0.1;
/** Polar angle: a touch above eye level, never from below. */
export const HEADSHOT_BETA_RAD = Math.PI / 2 - 0.04;
/** The crown rule: where the head is taken to be when no plausible head bone reading exists. */
export const HEADSHOT_CROWN_FRACTION = 0.87;
/** A head reading is plausible only inside this band of the body height. */
export const HEADSHOT_HEAD_BAND = { min: 0.5, max: 1.05 } as const;
/** The facings a RigDescriptor declares (rig-descriptor.ts) — the axis the body's face points along. */
export type RigFacing = '+Z' | '-Z' | '+X' | '-X';

/**
 * The ArcRotate azimuth that puts the camera ON the facing axis, looking
 * back at the face: +Z → π/2, −Z → −π/2 (World's own value for its −Z
 * stage), +X → 0, −X → π. Camera x = r·sinβ·cosα, z = r·sinβ·sinα.
 */
export function alphaForFacing(facing: RigFacing): number {
  switch (facing) {
    case '+Z':
      return Math.PI / 2;
    case '-Z':
      return -Math.PI / 2;
    case '+X':
      return 0;
    case '-X':
      return Math.PI;
  }
}

/** World's head-bone names, exact first; then the suffix rule. */
export const HEAD_BONE_NAMES: readonly string[] = ['Head', 'J_Bip_C_Head', 'mixamorig:Head', 'head'];
/** The loader strips reserved characters (`mixamorig:Head` → `mixamorigHead`); the runtime spelling is a head too. */
const HEAD_BONE_RUNTIME: readonly string[] = HEAD_BONE_NAMES.map((n) => n.replace(/[[\].:/]/g, ''));
/** A kept prefix (`Armature_head`) matches; `HeadTop_End` and `Forehead` do not. */
const HEAD_SUFFIX = /(^|[:_.])head$/i;

/** Is this node name the head, by World's rule — exact names (either spelling) or the suffix rule? */
export function isHeadBoneName(name: string): boolean {
  return HEAD_BONE_NAMES.includes(name) || HEAD_BONE_RUNTIME.includes(name) || HEAD_SUFFIX.test(name);
}

/**
 * Pick the head among a subject's node names, in World's order: the first
 * exact match wins over any suffix match, whatever the traversal order.
 */
export function pickHeadBone(names: readonly string[]): string | null {
  for (const exact of [...HEAD_BONE_NAMES, ...HEAD_BONE_RUNTIME]) {
    if (names.includes(exact)) return exact;
  }
  return names.find((n) => HEAD_SUFFIX.test(n)) ?? null;
}

export interface HeadshotInputs {
  /** Body height in metres — for a package, `heightMeters`. */
  readonly heightM: number;
  /** World-space Y of the head bone in metres, feet at 0 — or null when no head bone was found. */
  readonly headYM: number | null;
}

export interface HeadshotFrame {
  /** Where the head was taken from: the bone's reading, or the crown rule. */
  readonly headSource: 'bone' | 'crown';
  readonly headYM: number;
  readonly bottomM: number;
  readonly topM: number;
  readonly spanM: number;
  readonly radiusM: number;
  readonly targetYM: number;
  readonly fovRad: number;
  readonly betaRad: number;
}

/** Is a head reading inside World's plausibility band for this height? */
export function plausibleHeadY(heightM: number, headYM: number | null): headYM is number {
  return headYM !== null && Number.isFinite(headYM) && headYM > HEADSHOT_HEAD_BAND.min * heightM && headYM <= HEADSHOT_HEAD_BAND.max * heightM;
}

/** World's formula, in metres. Refuses a height that is not a positive number — the frame has nothing to be a fraction of. */
export function headshotFrame(inputs: HeadshotInputs): HeadshotFrame {
  const h = inputs.heightM;
  if (!Number.isFinite(h) || h <= 0) throw new Error(`headshot: a body height of ${String(inputs.heightM)} m frames nothing`);
  const plausible = plausibleHeadY(h, inputs.headYM);
  const headYM = plausible ? inputs.headYM : HEADSHOT_CROWN_FRACTION * h;
  const bottomM = headYM - HEADSHOT_BELOW_HEAD * h;
  const topM = (1 + HEADSHOT_HEADROOM) * h;
  const spanM = Math.max(HEADSHOT_MIN_SPAN_M, topM - bottomM);
  const radiusM = Math.max(HEADSHOT_MIN_RADIUS_M, (spanM / 2 / Math.tan(HEADSHOT_FOV / 2)) * HEADSHOT_MARGIN);
  return {
    headSource: plausible ? 'bone' : 'crown',
    headYM,
    bottomM,
    topM,
    spanM,
    radiusM,
    targetYM: (topM + bottomM) / 2,
    fovRad: HEADSHOT_FOV,
    betaRad: HEADSHOT_BETA_RAD,
  };
}

/** What the viewport measured of the subject, in RIG UNITS: the body at REST (World's scale rule), the head at the frame to be taken. */
export interface SubjectMeasure {
  /** The subject's lowest point at rest (its feet) — World grounds the body here. */
  readonly floorY: number;
  /** Rest bounding-box height — the height World's scale rule divides into `heightMeters`. */
  readonly heightUnits: number;
  /** World-space Y of the picked head bone AT THE FRAME (live), or null when none. */
  readonly headY: number | null;
  /** The node the head reading came from, for the status line. */
  readonly headBone: string | null;
  /** Horizontal centre of the subject, so the lens looks at the body and not at the file's origin. */
  readonly centerX: number;
  readonly centerZ: number;
}

/** A lens the viewport can place its capture camera from, in rig units — the live orbit is never touched. */
export interface HeadshotLens {
  readonly target: { readonly x: number; readonly y: number; readonly z: number };
  readonly radius: number;
  readonly fovRad: number;
  readonly betaRad: number;
  readonly alphaRad: number;
}

/**
 * The metre frame applied to THIS subject: World's scale ratio (declared
 * height over measured height) carries the head reading into metres and the
 * frame's radius and target back into units, seated on the subject's floor
 * and horizontal centre, the camera on the axis the rig FACES. Returns the
 * frame too, so the status line can say the numbers World would see.
 */
export function headshotLens(measure: SubjectMeasure, heightM: number, facing: RigFacing): { readonly lens: HeadshotLens; readonly frame: HeadshotFrame } {
  if (!(measure.heightUnits > 0)) throw new Error('headshot: the subject has no measurable height — nothing to frame');
  const metresPerUnit = heightM / measure.heightUnits;
  const headYM = measure.headY === null ? null : (measure.headY - measure.floorY) * metresPerUnit;
  const frame = headshotFrame({ heightM, headYM });
  const lens: HeadshotLens = {
    target: { x: measure.centerX, y: measure.floorY + frame.targetYM / metresPerUnit, z: measure.centerZ },
    radius: frame.radiusM / metresPerUnit,
    fovRad: frame.fovRad,
    betaRad: frame.betaRad,
    alphaRad: alphaForFacing(facing),
  };
  return { lens, frame };
}

/** Camera position for a lens, ArcRotate convention: target + radius·(sinβ·cosα, cosβ, sinβ·sinα). */
export function lensCameraPosition(lens: HeadshotLens): { x: number; y: number; z: number } {
  const sb = Math.sin(lens.betaRad);
  return {
    x: lens.target.x + lens.radius * sb * Math.cos(lens.alphaRad),
    y: lens.target.y + lens.radius * Math.cos(lens.betaRad),
    z: lens.target.z + lens.radius * sb * Math.sin(lens.alphaRad),
  };
}

/** The status line's account of a headshot take. */
export function headshotLine(frame: HeadshotFrame, measure: SubjectMeasure, heightM: number): string {
  const head = frame.headSource === 'bone' ? `head ${measure.headBone} at ${frame.headYM.toFixed(2)} m` : `no plausible head bone${measure.headBone !== null ? ` (${measure.headBone} read ${((measure.headY! - measure.floorY) * (heightM / measure.heightUnits)).toFixed(2)} m)` : ''} — crown rule ${frame.headYM.toFixed(2)} m`;
  return `headshot: ${heightM.toFixed(2)} m body · ${head} · frame ${frame.bottomM.toFixed(2)}–${frame.topM.toFixed(2)} m · radius ${frame.radiusM.toFixed(2)} m · World's lens (PR #1640)`;
}
